import { Loader2, Sparkles, AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import type { ParseErrorState } from "./types";

const PLACEHOLDER =
  "Paste a cruise/flight link, or describe it in plain words —\n" +
  "e.g. \"My wife and I are doing a 7-night Alaska cruise from Vancouver departing Sept 4, AA only.\"";

interface TripHubProps {
  description: string;
  onDescriptionChange: (value: string) => void;
  onPlan: () => void;
  parsing: boolean;
  coachNotes: string | null;
  error: ParseErrorState | null;
  /** While polling the async parse job, a status line to show under the button. */
  progressMessage?: string | null;
}

export default function TripHub({
  description,
  onDescriptionChange,
  onPlan,
  parsing,
  coachNotes,
  error,
  progressMessage,
}: TripHubProps) {
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="space-y-2">
          <Label htmlFor="trip-description" className="text-base font-semibold">
            Describe your trip — or paste a link
          </Label>
          <textarea
            id="trip-description"
            rows={4}
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={PLACEHOLDER}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            disabled={parsing}
            aria-describedby="trip-description-hint"
          />
          <p id="trip-description-hint" className="text-xs text-muted-foreground">
            We read your description and fill the sections below — you just tweak the blanks.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={onPlan} disabled={parsing} size="lg">
            {parsing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {parsing ? "Planning…" : "Plan my trip"}
          </Button>
          {parsing && progressMessage && (
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              {progressMessage}
            </p>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive-foreground"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
            <p className="text-foreground">{error.message}</p>
          </div>
        )}

        {coachNotes && !error && (
          <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-accent-foreground">Coach</p>
              <p className="whitespace-pre-line text-sm text-muted-foreground">{coachNotes}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
